/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useSelector } from 'react-redux';
import ProductFilter, { ComponentFilters } from '../features/products/components/ProductFilter';
import { useAppDispatch } from '../store/rootStore';
import { selectContactInfo, selectCurrentFarmerAccount } from '../features/azure-b2c/storage';
import ProductsSlice, {
  getPastProducts,
  selectPastProducts,
  selectCurrentStatus,
  Product,
} from '../features/products/storage';
import { filterProducts } from '../features/products/utils/filterProducts';
import PaginationCounter from '../components/Pagination/PaginationCounter';
import {
  PAGINATION_DATA_LIMIT,
  CATEGORY_DEFAULT_OPTION,
  SortingOptions,
  CONTENT_LAYOUT_WIDTH,
  // SORTING_OPTIONS,
} from '../util/consts';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { PageContainer } from '../components/layout/PageContainer';
import { TestIds } from '../testIds';
import { DateRangeType } from '../components/DateRangePicker';
import useQuoteProvider from '../features/quotes/components/QuoteModal/useQuoteModal';
import Section from '../components/layout/Section';
import PastProductsTab from '../features/products/pages/PastProductsTab';
import Tabs, { TabPanel } from '../components/Tabs';
import TitleBox from '../components/TitleBox';
import TypesenseSearch from '../features/typesense/Search';
import { useScrollRestoration } from '../util/useScrollRestoration';
import CartSlice, { selectCurrentCart } from '../features/cart/storage/cart.slice';
import { toast } from 'react-toastify';
import MaterialSymbol from '../components/MaterialSymbol';
import { selectStores } from '../features/stores/storage/stores.slice';
import { CartDeliveryMode } from '../features/cart/storage/cart.types';
import { useBranchLocationId } from '../util/useBranch';
import { StoreInfo } from '../features/stores/storage/stores.types';

const TypesenseFilters = [
  ComponentFilters.SEARCH_TYPESENSE,
  ComponentFilters.CATEGORY,
  ComponentFilters.PRICE,
  ComponentFilters.IN_STOCK,
];

const PastProductsFilters = [
  ComponentFilters.SEARCH_PAST,
  ComponentFilters.CATEGORY,
  ComponentFilters.DATE,
  ComponentFilters.PRICE,
];

const ProductsPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);

  const selectedAccount = useSelector(selectCurrentFarmerAccount);
  const data = useSelector(selectPastProducts);
  const status = useSelector(selectCurrentStatus);

  const cart = useSelector(selectCurrentCart);
  const account = useSelector(selectCurrentFarmerAccount);
  const stores: StoreInfo[] = useSelector(selectStores);

  const [categories, setCategories] = useState<string[]>([]);
  const [inStock, setInStock] = useState<boolean>(false);
  const branchId = useBranchLocationId();
  const [storeId, setStoreId] = useState<string | undefined>(branchId);

  const [category, setCategory] = useState(query.get('category') ?? CATEGORY_DEFAULT_OPTION);
  const [dateRange, setDateRange] = useState<DateRangeType>({
    from: (() => {
      const q = query.get('df');
      return q ? new Date(q) : undefined;
    })(),
    to: (() => {
      const q = query.get('dt');
      return q ? new Date(q) : undefined;
    })(),
  });

  const [firstTime, setFirstTime] = useState<boolean>(true);

  const [searchText, setSearchText] = useState(query.get('s') ?? '');
  const [searchInput, setSearchInput] = useState(query.get('s') ?? '');

  const getSortByDefault = () => {
    if (query.get('st') !== null) {
      return query.get('st') as string;
    }
    if (query.get('tab') && query.get('tab') === '0') {
      return SortingOptions.BestMatch.toString();
    }

    if (query.get('tab') && query.get('tab') === '1') {
      return SortingOptions.MostRecent.toString();
    }

    return SortingOptions.BestMatch.toString();
  };

  const [sortBy, setSortBy] = useState(getSortByDefault());
  const [priceRange, setPriceRange] = useState<{
    to: undefined | number;
    from: undefined | number;
  }>({
    from: (() => {
      const q = query.get('pf');
      return q ? parseInt(q) : undefined;
    })(),
    to: (() => {
      const q = query.get('pt');
      return q ? parseInt(q) : undefined;
    })(),
  });

  const pageType = parseInt(query.get('tab') ?? '0', 10);
  const [tab, setTab] = React.useState(pageType);
  const [currentPage, setCurrentPage] = useState(parseInt(query.get('page') ?? '1', 10));
  const relationshipOwner = useSelector(selectContactInfo);

  const ref = useRef<HTMLElement>(null);
  useScrollRestoration({
    ref,
    scrollYPositionKey: sessionStorage.scrollYPositionOnProducts,
    path: '/products/',
  });

  // Auto scroll to top if account changes, or if page changes
  useEffect(() => {
    const element = document.getElementById(TestIds.pages.mainScrollContainer);
    if (element) {
      element.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, [currentPage, selectedAccount]);

  useEffect(() => {
    if (selectedAccount && status === 'idle') {
      setQueryParams();
      ProductsSlice.actions.initPastProducts({ accountNumber: selectedAccount.accountNumber });
      dispatch(
        getPastProducts({
          accountNumber: selectedAccount.accountNumber,
        }),
      );
    }
  }, [selectedAccount?.accountNumber, status]);

  useEffect(() => {
    setCurrentPage(parseInt(query.get('page') ?? '1', 10));
  }, [query.get('page')]);

  const setQueryParams = () => {
    let page = 1;
    if (firstTime) {
      page = currentPage;
      setFirstTime(false);
    } else setCurrentPage(1);

    const params = createSearchParams({
      tab: tab.toString(),
      s: searchText,
      category: category,
      dt: dateRange.to?.toISOString() ?? '',
      df: dateRange.from?.toISOString() ?? '',
      pt: priceRange.to?.toString() ?? '',
      pf: priceRange.from?.toString() ?? '',
      st: sortBy,
      page: page.toString(),
    });

    navigate({
      pathname: '/products',
      search: params.toString(),
    });
  };

  useEffect(() => {
    setQueryParams();
  }, [category, dateRange, searchText, priceRange, tab, sortBy]);

  const handleClearFilters = () => {
    setCategory(CATEGORY_DEFAULT_OPTION);
    setDateRange({ from: undefined, to: undefined });
    setPriceRange({ from: undefined, to: undefined });
    setSearchText('');
    setSearchInput('');
    setInStock(false);
  };

  const selectedFilters = useMemo(() => {
    return {
      category: { value: category, setValue: setCategory },
      dateRange: { value: dateRange, setValue: setDateRange },
      searchText: { value: searchText, setValue: setSearchText },
      searchInput: { value: searchInput, setValue: setSearchInput },
      priceRange: { value: priceRange, setValue: setPriceRange },
      inStock: { value: inStock, setValue: setInStock },
      storeId: { value: storeId, setValue: setStoreId },
    };
  }, [
    category,
    dateRange,
    inStock,
    storeId,
    priceRange,
    searchText,
    searchInput,
    setCategory,
    setDateRange,
    setInStock,
    setStoreId,
    setPriceRange,
    setSearchText,
    setSearchInput,
  ]);

  useEffect(() => {
    if (!cart?.pickupStore && inStock && account?.homeBranchId) {
      const store = stores.find((store) => store.inventorySiteId === account.homeBranchId);
      if (store) {
        if (cart?.cartDeliveryMode !== CartDeliveryMode.DELIVERY) {
          toast(`Pickup store set to ${store.orgUnitName}`, {
            type: 'success',
            autoClose: 5000,
            icon: <MaterialSymbol>check</MaterialSymbol>,
          });
        }
        dispatch(
          CartSlice.actions.setPickUpStore({
            accountNumber: account.accountNumber,
            pickupStore: store,
          }),
        );
      }
    }
  }, [branchId, account?.hasTfoAccess, inStock, cart?.pickupStore, cart?.cartDeliveryMode]);

  const isFilterApplied = useMemo(() => {
    return !(
      !selectedFilters.inStock.value &&
      selectedFilters.category.value === CATEGORY_DEFAULT_OPTION &&
      (selectedFilters.dateRange.value.from === undefined ||
        selectedFilters.dateRange.value.to === undefined) &&
      (selectedFilters.priceRange.value.from === undefined ||
        selectedFilters.priceRange.value.to === undefined) &&
      selectedFilters.searchText.value === ''
    );
  }, [selectedFilters]);

  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);

  useEffect(() => {
    const freshFilteredProducts = filterProducts(
      data,
      {
        category: selectedFilters.category.value,
        searchText: selectedFilters.searchText.value,
        dateRange: selectedFilters.dateRange.value,
        priceRange: selectedFilters.priceRange.value,
      },
      sortBy as SortingOptions,
    );

    setFilteredProducts(freshFilteredProducts);
  }, [
    data,
    sortBy,
    selectedFilters.category.value,
    selectedFilters.searchText.value,
    selectedFilters.dateRange.value,
    selectedFilters.priceRange.value,
    selectedFilters.inStock.value,
  ]);

  const paginatedData = React.useMemo(() => {
    const startIndex = currentPage * PAGINATION_DATA_LIMIT - PAGINATION_DATA_LIMIT;
    const endIndex = startIndex + PAGINATION_DATA_LIMIT;
    const products = [...filteredProducts].slice(startIndex, endIndex);
    return products;
  }, [filteredProducts, currentPage]);

  const noPagination = filteredProducts.length <= PAGINATION_DATA_LIMIT;
  const paginationCounter = useMemo(() => {
    return (
      <PaginationCounter
        data-testid={TestIds.components.pagination.number}
        label='products'
        itemsPerPage={PAGINATION_DATA_LIMIT}
        count={paginatedData.length}
        total={filteredProducts.length}
        currentPage={currentPage}
      />
    );
  }, [paginatedData, filteredProducts.length, currentPage]);

  const tabsInfo = [
    {
      label: 'All products',
      'data-testid': TestIds.feat.product.tabView.allProductsTab,
      details: (
        <TypesenseSearch
          accountNumber={selectedAccount?.accountNumber}
          setCategoriesCallback={setCategories}
          paginationCounter={paginationCounter}
          status={status}
          data={data}
          handleClearFilters={handleClearFilters}
          isFilterApplied={isFilterApplied}
          sortBy={sortBy}
          setSortBy={setSortBy}
          paginatedData={paginatedData}
          selectedFilters={selectedFilters}
          currentPage={currentPage}
          relationshipOwner={relationshipOwner}
        />
      ),
      default: pageType === 0,
    },
    {
      label: 'Previously ordered',
      'data-testid': TestIds.feat.product.tabView.pastProductsTab,
      details: (
        <PastProductsTab
          setCategoriesCallback={setCategories}
          paginationCounter={paginationCounter}
          status={status}
          data={data}
          filteredProducts={filteredProducts}
          handleClearFilters={handleClearFilters}
          isFilterApplied={isFilterApplied}
          sortBy={sortBy}
          setSortBy={setSortBy}
          paginatedData={paginatedData}
          selectedFilters={selectedFilters}
          noPagination={noPagination}
          currentPage={currentPage}
          relationshipOwner={relationshipOwner}
        />
      ),
      default: pageType === 1,
    },
  ];

  const tabPanels = () => {
    return (
      <Section width={'100%'}>
        <TitleBox display='Shop' />
        <Box>
          <Tabs
            value={tab}
            setValue={setTab}
            tabsInfo={tabsInfo}
            aria-label='Product search tabs'
            id='product-tabs'
          />
        </Box>
        {tab === 0 ? (
          <Box pb={2}>
            <ProductFilter
              components={TypesenseFilters}
              filters={{ categories }}
              selectedFilters={selectedFilters}
              items={data}
            />
          </Box>
        ) : (
          <Box pb={2}>
            <ProductFilter
              components={PastProductsFilters}
              filters={{ categories }}
              selectedFilters={selectedFilters}
              items={data}
            />
          </Box>
        )}
      </Section>
    );
  };

  return (
    <PageContainer
      data-testid={TestIds.pages.pastProducts}
      sx={{ maxWidth: `${CONTENT_LAYOUT_WIDTH}px` }}
    >
      {tabPanels()}
      {tabsInfo.map((x, index) => (
        <TabPanel key={`${x.label}-${tab}`} value={tab} index={index} id={'product-panels'}>
          {x.details}
        </TabPanel>
      ))}
    </PageContainer>
  );
};

export default () => useQuoteProvider(ProductsPage);
